import {
  AloftDate,
  AloftDateJSON,
  CompsListingsSearchCriteria,
  MLSListing,
  PropertyData,
  RecommendedCompsStatistics,
  SubjectData,
  expected,
  isDefined,
} from '@aloft/toolkit-shared';
import { Block, Flex, Modal } from '@aloft/toolkit-sui';
import { SortingState } from '@tanstack/react-table';

import { SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { useAutofillProperty } from '../../hooks/useAutofillProperty.ts';
import { useToaster } from '../../hooks/useToaster/useToaster.tsx';
import { Tkv2ReferenceViewDrawer } from '../../modules/referenceView/Tkv2ReferenceViewDrawer.tsx';
import { removeComps } from '../../redux/features/actions/removeComp.ts';
import { featuresSelectors } from '../../redux/features/selectors/featuresSelectors.ts';
import { selectEffectiveDate, selectOrder } from '../../redux/features/selectors/orderSelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import {
  LocationType,
  formatProximityRequirementTooltip,
  getLocationTypeProximityRequirementMiles,
} from '../../utils/locationTypeProximityRequirementMiles.ts';
import { proximityMiles } from '../../utils/proximityMiles.ts';
import { LoadingScreenSamFull } from '../LoadingScreen/LoadingScreenSamFull.tsx';
import { Tkv2ReferenceBar } from '../ReferenceBar/Tkv2ReferenceBar.tsx';
import { RecommendedCompsModalFooterToolbar } from './RecommendedCompsModalFooterToolbar.tsx';
import { useRecommendedCompsInternalState } from './hooks/useRecommendedCompsInternalState.ts';
import { useRecommendedCompsFilterRoute } from './hooks/useRecommendedCompsFilterRoute.ts';
import { useRecommendedProperties } from './hooks/useRecommendedProperties.ts';
import { FilterDrawer } from './internal/FilterDrawer/FilterDrawer.tsx';
import { dismissDefaultFiltersInfo } from './internal/FilterDrawer/defaultFiltersInfoToastStorage.ts';
import { RentalRecommendedCompSelection } from './internal/RentalRecommendedCompSelection.tsx';
import { CompSelectionState, FiltersMessage, InitialCompSearch, schCompSearch } from './types.ts';
import { handleAddClickForCompCreation } from './utils/handleAddClickForCompCreation.ts';
import { loadExistingSearchCriteria } from './utils/loadExistingSearchCriteria.ts';

const { selectShouldShowRecommendedCompsFiltering } = featuresSelectors;

const getSelectedIDs = (
  rowSelection: CompSelectionState,
  isInspectifyOrder: boolean,
  hasArvOption: boolean,
): string[] => {
  if (isInspectifyOrder && hasArvOption) {
    return Object.entries(rowSelection)
      .filter(([, value]) => value.aiv || value.arv)
      .map(([key]) => key);
  }
  return Object.keys(rowSelection);
};

type RentalModalProps = {
  createComps: (props: PropertyData[]) => void;
  currentComps: Record<string, PropertyData>;
  rentalCompSeq: string[];
  orderID: string;
  isModalOpen: boolean;
  onModalClose: () => void;
  subject: SubjectData;
  effectiveDate: AloftDateJSON;
};

export const RecommendedRentalCompsModal = ({
  createComps,
  currentComps,
  rentalCompSeq,
  orderID,
  isModalOpen,
  onModalClose,
  subject,
  effectiveDate,
}: RentalModalProps) => {
  const dispatch = useDispatch();
  const makeToast = useToaster();
  const { isLoading, data, getProperties, isError } = useRecommendedProperties({
    orderID,
    compType: 'rental',
  });

  const order = useAppSelector((state) => selectOrder(state));
  const { hasArvOption } = order;
  const featureAllowRecommendedCompsFiltering = useAppSelector((state) =>
    selectShouldShowRecommendedCompsFiltering(state),
  );
  const allowRecommendedCompsFiltering =
    featureAllowRecommendedCompsFiltering || order.isInspectifyOrder;
  const autofillProperty = useAutofillProperty();
  const effectiveYear = AloftDate.schema
    .parse(expected(useAppSelector(selectEffectiveDate)))
    .getYear();
  const [isAddCompsLoading, setIsAddCompsLoading] = useState(false);
  const [newFiltersApplied, setNewFiltersApplied] = useState(false);
  const [filtersMessage, setFiltersMessage] = useState<FiltersMessage | undefined>(undefined);
  const [allowApplyNewFilters, setAllowApplyNewFilters] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<CompSelectionState>({});
  const [hasInitializedSelection, setHasInitializedSelection] = useState(false);
  const previousDataRef = useRef<typeof data>();
  const ref = useRef<{ downloadCSV: () => void; getQualityTiersBreakdown: () => unknown } | null>(
    null,
  );

  const foundComps = data?.body?.status !== undefined;
  const showUpdateRentalCompsButton = foundComps && !isLoading;
  const showCancelButton = !isAddCompsLoading;

  const { form, showNoRecommendations, setShowNoRecommendations, getDefaultFormValues } =
    useRecommendedCompsInternalState(subject, AloftDate.schema.parse(effectiveDate));

  useEffect(() => {
    const subscribe = form.subscribe({
      formState: {
        values: true,
      },
      callback: (state) => {
        const stateChangedFromOriginal =
          JSON.stringify(state.values.originalSearch) !== JSON.stringify(state.values.search);

        const { geoSearch } = state.values.search;
        const geoSearchValid =
          (geoSearch.t === 'polygon' && geoSearch.coords.length > 0) ||
          (geoSearch.t === 'radius' && geoSearch.radiusInMeters > 0);
        setAllowApplyNewFilters(stateChangedFromOriginal && geoSearchValid);
      },
    });

    return subscribe;
  }, [form]);

  const { isFilterDrawerOpen, onFilterDrawerOpen, onFilterDrawerClose } =
    useRecommendedCompsFilterRoute();

  const handleFilterDrawerClose = () => {
    onFilterDrawerClose();
    form.setValue('search', form.getValues('originalSearch'));
  };

  const applyFilters = () => {
    setShowNoRecommendations(false);
    getProperties({
      search: schCompSearch.parse(form.getValues('search')),
    });
    setNewFiltersApplied(true);
    setFiltersMessage(undefined);
    setAllowApplyNewFilters(false);
    dismissDefaultFiltersInfo(orderID);
  };

  const currentCompsSet = useMemo(
    (): Record<string, string> =>
      Object.entries(currentComps)
        .map(([compId, comp]) => {
          const toolkitId = comp.readonlyData.backupListingData?.listing?.ToolkitListingID;
          return toolkitId ? [toolkitId as string, compId] : null;
        })
        .filter((entry): entry is [string, string] => entry !== null)
        .reduce<Record<string, string>>((acc, [toolkitId, compId]) => {
          acc[toolkitId] = compId;
          return acc;
        }, {}),
    [currentComps],
  );

  const { setValue } = form;
  let existingSearchCriteria: CompsListingsSearchCriteria | undefined;
  let statistics: RecommendedCompsStatistics | undefined;

  if (
    data?.source === 'abode' &&
    data.body &&
    'output' in data.body &&
    data.body.status === 'found'
  ) {
    existingSearchCriteria = data.body.output.searchCriteria;
    statistics = data.body.output.statistics;
  }

  useEffect(() => {
    if (isLoading) return;

    if (
      isError ||
      (data?.body &&
        typeof data.body === 'object' &&
        'status' in data.body &&
        data.body.status === 'error')
    ) {
      setFiltersMessage({
        type: 'error',
        message: 'Failed to load recommended comps. Please try again or update your filters.',
        color: 'alertError',
        icon: 'warning',
      });
    } else if (!newFiltersApplied) {
      setFiltersMessage(undefined);
    } else if (
      data?.source === 'abode' &&
      data.body &&
      'status' in data.body &&
      data.body.status === 'found' &&
      data.body.output.resultsCount > 100
    ) {
      setFiltersMessage({
        type: 'warning',
        message: 'Too many results, consider narrowing your filters for better focus.',
        color: 'statusWarning',
        icon: 'warning',
      });
    } else if (data?.body && 'status' in data.body && data.body.status === 'not_found') {
      setFiltersMessage({
        type: 'warning',
        message: 'No comps found with the current filters. Try broadening your search criteria.',
        color: 'statusWarning',
        icon: 'warning',
      });
    } else if (newFiltersApplied && !allowApplyNewFilters) {
      setFiltersMessage({
        type: 'success',
        message: 'Filters applied!',
        color: 'alertSuccess',
        icon: 'check',
      });
    } else {
      setFiltersMessage(undefined);
    }
  }, [data, isLoading, newFiltersApplied, isError, allowApplyNewFilters]);

  useEffect(() => {
    if (data && !isError && data !== previousDataRef.current) {
      previousDataRef.current = data;

      let search = {} as InitialCompSearch['body']['search'];
      if (existingSearchCriteria) {
        search = loadExistingSearchCriteria(existingSearchCriteria, subject, getDefaultFormValues);
      } else {
        search = getDefaultFormValues();
      }
      setValue('search', search);
      setValue('originalSearch', search);
    }
  }, [data, isError, existingSearchCriteria, subject, getDefaultFormValues, setValue]);

  useEffect(() => {
    if (data && !isError) {
      const currentCompsKeys = Object.keys(currentCompsSet || {});
      if (currentCompsKeys.length > 0 && !hasInitializedSelection) {
        if (order.isInspectifyOrder && hasArvOption) {
          setRowSelection((prevSelection) => {
            const newSelection: CompSelectionState = { ...prevSelection };
            currentCompsKeys.forEach((listingId) => {
              const compId = currentCompsSet[listingId];
              const comp = compId ? currentComps[compId] : undefined;
              if (comp) {
                const existing = newSelection[listingId] || {};
                newSelection[listingId] = {
                  aiv: existing.aiv || comp.compType === 'aiv',
                  arv: existing.arv || comp.compType === 'arv',
                };
              }
            });
            Object.values(currentComps).forEach((comp) => {
              const listingId =
                comp.readonlyData.backupListingData?.listing?.ToolkitListingID ||
                comp.readonlyData.backupListingData?.listing?.ListingId;
              if (listingId) {
                const existing = newSelection[listingId] || {};
                newSelection[listingId] = {
                  aiv: existing.aiv || comp.compType === 'aiv',
                  arv: existing.arv || comp.compType === 'arv',
                };
              }
            });
            return newSelection;
          });
        } else {
          setRowSelection(() => {
            const newSelection: CompSelectionState = {};
            currentCompsKeys.forEach((listingId) => {
              newSelection[listingId] = {};
            });
            return newSelection;
          });
        }
        setHasInitializedSelection(true);
      }
    }
  }, [
    data,
    isError,
    currentCompsSet,
    currentComps,
    hasInitializedSelection,
    order.isInspectifyOrder,
    hasArvOption,
  ]);

  const closeModal = () => {
    setRowSelection({});
    setHasInitializedSelection(false);
    onFilterDrawerClose();
    onModalClose();
  };

  const handleSetRowSelection = (newSet: SetStateAction<CompSelectionState>) => {
    const resolvedValue = typeof newSet === 'function' ? newSet(rowSelection) : newSet;
    Object.keys(resolvedValue).forEach((key) => {
      const selection = resolvedValue[key];
      if (selection && !selection?.listingData && data?.body && data.body.status === 'found') {
        const listing = data.body.output.results.find(
          (result) =>
            ('ToolkitListingID' in result && result.ToolkitListingID === key) ||
            ('ListingId' in result && result.ListingId === key),
        );
        if (listing) {
          selection.listingData = listing as MLSListing;
        }
      }
    });
    setRowSelection(resolvedValue);
  };

  const handleAddClick = async () => {
    setIsAddCompsLoading(true);
    try {
      if (!data?.body || data.body.status !== 'found') {
        console.warn('No rental recommended comps attachment found');
        return;
      }

      const selectedIDs = getSelectedIDs(rowSelection, order.isInspectifyOrder, hasArvOption);
      const compsToAdd = selectedIDs.filter((id) => !currentCompsSet[id] && id !== 'subject');

      const locationType = subject.editableData.propertyIdentification.locationType as
        | LocationType
        | undefined;
      const proximityRequirementMiles = getLocationTypeProximityRequirementMiles(locationType);
      const subjectCoordinates = subject.editableData.propertyIdentification.coordinates;

      if (
        locationType &&
        proximityRequirementMiles !== undefined &&
        subjectCoordinates &&
        compsToAdd.length > 0 &&
        data.body.output.results
      ) {
        const outOfRangeCount = data.body.output.results.filter((listing) => {
          const id = listing.ToolkitListingID || listing.ListingId;
          if (!id || !compsToAdd.includes(id)) return false;
          if (listing.Latitude === undefined || listing.Longitude === undefined) return false;
          const miles = proximityMiles(subjectCoordinates, {
            latitude: listing.Latitude,
            longitude: listing.Longitude,
          });
          return miles !== undefined && miles > proximityRequirementMiles;
        }).length;

        if (outOfRangeCount > 0) {
          makeToast({
            id: `proximity-requirement-exceeded-rental-${orderID}`,
            type: 'warning',
            title: 'Proximity warning',
            body: formatProximityRequirementTooltip(locationType, proximityRequirementMiles),
          });
        }
      }

      const newRowSelection: CompSelectionState = compsToAdd.reduce<CompSelectionState>(
        (acc, id) => {
          acc[id] = rowSelection[id] || {};
          return acc;
        },
        {},
      );

      await handleAddClickForCompCreation({
        isLoading,
        rowSelection: newRowSelection,
        createComps,
        autofillProperty,
        effectiveYear,
        orderID,
        isInspectifyOrder: order.isInspectifyOrder,
        hasArvOption,
        listingType: 'rentalListing',
      });

      const compsToDelete = Object.keys(currentCompsSet)
        .filter((id) => !selectedIDs.includes(id) && id !== 'subject')
        .map((id) => currentCompsSet[id])
        .filter(isDefined);

      if (compsToDelete.length > 0) {
        dispatch(removeComps({ ids: compsToDelete, sequenceTarget: 'rentalCompSequence' }));
      }

      closeModal();
    } catch (e: unknown) {
      console.error(e, 'Failed to add recommended rental comps.');
      throw e;
    } finally {
      setIsAddCompsLoading(false);
    }
  };

  return (
    <FormProvider {...form}>
      <LoadingScreenSamFull
        isLoading={isAddCompsLoading || (isLoading && !isFilterDrawerOpen)}
        what="comps"
        wrapperProps={{ backgroundColor: 'surface1' }}
      />
      <Modal
        size="full"
        isOpen={isModalOpen}
        onClose={closeModal}
        closeOnEsc
        showCloseButton={false}
        primaryAction={
          showUpdateRentalCompsButton
            ? {
                id: 'add-rental-btn',
                text: 'Update Rental Comps',
                onClick: () => {
                  void handleAddClick();
                },
                __dangerDoNotUseSx: { padding: '0 14px' },
              }
            : undefined
        }
        secondaryAction={
          showCancelButton
            ? {
                text: 'Cancel',
                onClick: closeModal,
                variant: 'inline',
                marginRight: '1rem',
                __dangerDoNotUseSx: { padding: '0 14px' },
              }
            : undefined
        }
        modalBodyProps={{ paddingRight: 0, overflow: 'visible' }}
        leftFooterContent={
          allowRecommendedCompsFiltering &&
          !isLoading && (
            <RecommendedCompsModalFooterToolbar onFilterDrawerOpen={onFilterDrawerOpen} />
          )
        }
      >
        {allowRecommendedCompsFiltering && (
          <FilterDrawer
            isOpen={isFilterDrawerOpen}
            onCancel={handleFilterDrawerClose}
            onApply={applyFilters}
            subject={subject}
            effectiveDate={effectiveDate}
            isLoading={isLoading}
            statistics={statistics}
            filtersMessage={filtersMessage}
            showSaleDateWarning={allowApplyNewFilters || newFiltersApplied}
            allowApplyNewFilters={allowApplyNewFilters}
            orderID={orderID}
          />
        )}

        <Block height="calc(100vh - 120px)" marginTop="-20px">
          <Flex height="100%" width="100%">
            <Tkv2ReferenceViewDrawer>
              <RentalRecommendedCompSelection
                orderID={orderID}
                subject={subject}
                comps={currentComps}
                compSeq={rentalCompSeq}
                effectiveDate={effectiveDate}
                sorting={sorting}
                setSorting={setSorting}
                rowSelection={rowSelection}
                setRowSelection={handleSetRowSelection}
                currentComps={currentCompsSet}
                showNoRecommendations={showNoRecommendations}
                setShowNoRecommendations={setShowNoRecommendations}
                getDefaultFormValues={getDefaultFormValues}
                isInspectifyOrder={order.isInspectifyOrder}
                hasArvOption={hasArvOption}
                allowRecommendedCompsFiltering={allowRecommendedCompsFiltering}
                ref={ref}
              />
            </Tkv2ReferenceViewDrawer>
            <Block marginTop="-20px">
              <Tkv2ReferenceBar />
            </Block>
          </Flex>
        </Block>
      </Modal>
    </FormProvider>
  );
};
